import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { ErrorStatusEnum } from '@app/common';
import styles from '../blocks/web/homepage-sections.module.css';
import { withAuth } from '../models/functions/with-auth';
import { Layout } from '../blocks/web/layout/layout';
import { IntroHomepageMarketing } from '../blocks/web/intro/homepage/marketing/intro-homepage-marketing';
import { Partners } from '../components/web/partners/partners';
import { FeatureList } from '../blocks/web/feature-list/feature-list';
import { HomepageProcess } from '../blocks/web/homepage-process/homepage-process';
import { Community } from '../blocks/web/community/community';
import { Cta } from '../blocks/web/cta/cta';
import { HomepagePricing } from '../blocks/web/pricing/homepage-pricing';
import { routes } from '../config/routes';
import { useRootStore } from '../providers/root-store-provider';
import { FaqTop } from '../blocks/web/faq/top/faq-top';
import { Testimonials } from '../blocks/web/testimonials/testimonials';
import { createWebServerSideProps } from '../models/functions/create-web-server-side-props';
import { getBooleanQueryParam } from '../utils/get-boolean-query-param';

export const IndexWeb: React.FC = () => {
    const { t } = useTranslation();
    const rootStore = useRootStore();
    const { alertStore, authStore, themeStore } = rootStore;
    const router = useRouter();

    useEffect(() => {
        const handleQuery = async () => {
            const shouldOpenSignup = getBooleanQueryParam(router.query.signup);
            const shouldOpenPromo = getBooleanQueryParam(router.query.predepissimonery);
            const isCampaignSignup = router.query.signupSource === 'campaign';

            if (shouldOpenPromo) {
                rootStore.cookieService.setPredepissimoneryPromo(true);
            }

            // handle logout
            if (router.query.logout) {
                authStore.logout();
                await router.replace(routes.web.index);
                themeStore.setDefaultTheme();
                return;
            }

            // handle errors from server side (get server side props)
            if (router.query.status && router.query.status.length) {
                await router.push(routes.web.index);
                alertStore.setErrorMessageByStatus(router.query.status as ErrorStatusEnum);
                return;
            }

            // handle signup modal from affiliate link and promo campaign
            if (shouldOpenSignup || shouldOpenPromo) {
                if (rootStore.cookieService.hasJwtToken()) {
                    authStore.authDialogStore.setAlreadyLoggedInActive();
                } else {
                    if (isCampaignSignup) {
                        rootStore.cookieService.clearRedirectUrl();
                    }
                    authStore.authDialogStore.setShowSignupPromo(
                        shouldOpenPromo || rootStore.cookieService.hasPredepissimoneryPromo(),
                    );
                    authStore.authDialogStore.setSignupActive();
                }
                await router.replace(routes.web.index, undefined, { shallow: true });
            }
        };
        handleQuery();
    }, [router.query]);

    return (
        <Layout
            intro={
                <div className={styles.hero}>
                    <IntroHomepageMarketing />
                    <Partners />
                </div>
            }
            introAppearance="light"
        >
            <div className={styles.sections}>
                <HomepageProcess />
                <FeatureList />

                <Testimonials />

                <Community />

                <FaqTop />

                <Cta
                    compact
                    titleGrey={t('pages.homepage.cta.title')}
                    title={t('pages.homepage.cta.subtitle')}
                    buttonText={t('pages.homepage.cta.button')}
                    signup
                />

                <HomepagePricing />
            </div>
        </Layout>
    );
};

export const getServerSideProps = createWebServerSideProps(true);

export default withAuth(IndexWeb, false);
